import { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Card, Text } from '@change/ds-core-ui';
import { CheckIcon } from '@change/ds-icons/CheckIcon';
import { CopyIcon } from '@change/ds-icons/CopyIcon';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { SocialIconWithSuccessState } from '#src/app/shared/components/share';
import { useCarouselScrollFade } from '#src/app/shared/hooks/carousel';

import { useShareActions } from '../../../ShareActions/hooks/useShareActions';

import { useCarouselScrollTracking } from './hooks/useCarouselScrollTracking';

const COPIED_RESET_MS = 2500;

/**
 * The share link is shown host + path only, dropping the protocol and query string so the truncated URL stays readable.
 */
function getVisibleUrl(url: string, errorReporter: UtilityContext['errorReporter']): string {
	try {
		const parsed = new URL(url);
		return `${parsed.host}${parsed.pathname}`;
	} catch (error) {
		void errorReporter.report({
			error: new Error('Failed to parse share URL for display', { cause: error }),
			params: { url },
		});
		return url;
	}
}

type Props = {
	onShareSuccess: () => void;
};

export const GoalShareActions = ({ onShareSuccess }: Props) => {
	const { translate } = useI18n();
	const { resolveUrl, errorReporter } = useUtilityContext();
	const { data } = useShareActions();
	const [copied, setCopied] = useState(false);
	const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const { first, rest, mobileShareVisual } = data;

	const { ref: carouselRef, showFade: showScrollFade } = useCarouselScrollFade(rest.length);
	const handleCarouselScroll = useCarouselScrollTracking();

	const handleAfterCopy = useCallback(() => {
		setCopied(true);
		if (resetTimerRef.current != null) clearTimeout(resetTimerRef.current);
		resetTimerRef.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
	}, []);

	useEffect(
		() => () => {
			if (resetTimerRef.current != null) clearTimeout(resetTimerRef.current);
		},
		[],
	);

	if (!first && rest.length === 0 && !mobileShareVisual) return null;

	const copyButtonIcon = copied ? (
		<CheckIcon data-qa="copied-icon-check" className="text-icon-success dark:text-icon-strong-fixed h-5! w-5!" />
	) : (
		<CopyIcon className="h-5! w-5!" />
	);

	return (
		<div className="flex flex-col gap-6">
			{first && (
				<Card elevation className="bg-background-base flex items-center justify-between gap-3 rounded-lg p-4 pl-5">
					<div className="relative min-w-0 flex-1">
						<Text as="div" fontWeight="bold" className="text-typography-strong text-xs leading-5">
							{translate('corgi.psf.share.sharing_goal.share_link_label')}
						</Text>
						<div className="text-typography-weak overflow-hidden text-xs leading-5 text-ellipsis whitespace-nowrap">
							{getVisibleUrl(resolveUrl(first.url), errorReporter)}
						</div>
						<div
							aria-hidden
							className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-[linear-gradient(to_right,transparent,var(--color-background-base))]"
						/>
					</div>
					<first.ShareAction
						onAfterAction={(success) => {
							if (success) {
								handleAfterCopy();
								onShareSuccess();
							}
						}}
					>
						<Button
							iconOnly
							variant="primary"
							size="x-small"
							aria-label={translate('fe.components.psf.share.buttons.copy_link')}
							leadingIcon={copyButtonIcon}
							className="h-11 w-11 p-0"
							data-qa="psf-share-copy-button"
						/>
					</first.ShareAction>
				</Card>
			)}

			{mobileShareVisual && (
				<img
					src={mobileShareVisual}
					alt={translate('corgi.psf.share.share_visual_preview_alt')}
					className="mx-auto my-2 block max-w-61 rounded-md"
					data-qa="psf-share-mobile-asset"
				/>
			)}

			{rest.length > 0 && (
				// Bleed to the card's right edge (left stays aligned) so the next channel is
				// partially visible, signalling the row scrolls horizontally — matches Figma 2983-2810.
				<div
					ref={carouselRef}
					onScroll={handleCarouselScroll}
					className={cn(
						'-mr-6 flex snap-x snap-proximity [scrollbar-width:none] overflow-x-auto overscroll-contain py-1 md:-mr-10 [&::-webkit-scrollbar]:hidden',
						showScrollFade &&
							'[mask-image:linear-gradient(to_right,black_calc(100%_-_72px),transparent)] [-webkit-mask-image:linear-gradient(to_right,black_calc(100%_-_72px),transparent)]',
					)}
				>
					{rest.map((actionItem) => (
						<div key={actionItem.channel} className="w-20 max-w-20 shrink-0 snap-start">
							<SocialIconWithSuccessState
								data-qa={`psf-share-${actionItem.channel}-button`}
								buttonConfig={actionItem}
								className="w-20 max-w-20 text-center"
								onAfterShare={(success) => {
									if (success) onShareSuccess();
								}}
							/>
						</div>
					))}
				</div>
			)}
		</div>
	);
};
